/**
 * Shared formatting utilities.
 */

/**
 * Format a number as compact currency ($1.2M, $15K, $1,750, $99).
 * Handles negative values. Optionally prefixes positive values with "+".
 *
 * Sub-10K values render with thousands separators rather than rounding to the
 * nearest $1K — at that scale the rounding loses meaningful precision (e.g.
 * $1,750 → "$2K").
 */
export function formatCurrency(
  value: number,
  options?: { signed?: boolean },
): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : options?.signed ? "+" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${Math.round(abs).toLocaleString("en-US")}`;
}

/**
 * Format a number as compact without currency symbol (1.2M, 15K, 1,750, 99).
 * Useful for AI prompt strings where the dollar sign is added separately.
 */
export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${Math.round(abs).toLocaleString("en-US")}`;
}

/**
 * Format a large number as compact currency using B / M / K suffixes.
 * Matches the original Organization.tsx `formatMoneyShort` helper.
 *
 * Examples: $1.2B · $3.4M · $15K · $999
 */
export function formatMoneyShort(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}
